import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  CalendarRange,
  Download,
  Globe,
  Loader2,
  MapPin,
  RotateCcw,
  Search,
  UserMinus,
  UserPlus,
  Users,
  Wifi,
} from 'lucide-react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CSVLink } from 'react-csv';
import { adminAPI } from '../services/api';

const RANGE_OPTIONS = [
  { value: '1d', label: 'Last 1 day' },
  { value: '2d', label: 'Last 2 days' },
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

const PURPLE_SCALE = ['#f5f3ff', '#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function relativeTime(value) {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  if (diff < 60 * 1000) return 'Just now';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} hours ago`;
  return new Date(value).toLocaleString();
}

function Change({ value }) {
  if (value == null) return <span className="text-xs text-gray-400">No prior period</span>;
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(value)}% vs previous
    </span>
  );
}

function heatColor(count, max) {
  if (!max || count <= 0) return PURPLE_SCALE[0];
  const idx = Math.min(PURPLE_SCALE.length - 1, Math.ceil((count / max) * (PURPLE_SCALE.length - 1)));
  return PURPLE_SCALE[idx];
}

function UserActivity() {
  const [range, setRange] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [citySearch, setCitySearch] = useState('');
  const [citySort, setCitySort] = useState('count_desc');
  const [selectedCities, setSelectedCities] = useState([]);
  const [focusCity, setFocusCity] = useState('Pune');
  const [focusSeries, setFocusSeries] = useState({
    activeCount: true,
    newCount: true,
    returningCount: true,
  });
  const citiesInitialized = React.useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchActivity = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        range,
        page,
        limit,
        search: search || undefined,
        city: cityFilter || undefined,
        status: statusFilter,
        verified: verifiedFilter,
        citySearch: citySearch || undefined,
        citySort,
        cities: selectedCities.join(',') || undefined,
      };
      if (range === 'custom') {
        params.start = customStart;
        params.end = customEnd;
      }
      const response = await adminAPI.getUserActivity(params);
      setData(response.data.userActivity);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load user activity');
    } finally {
      setLoading(false);
    }
  }, [range, customStart, customEnd, page, limit, search, cityFilter, statusFilter, verifiedFilter, citySearch, citySort, selectedCities]);

  useEffect(() => {
    if (range === 'custom' && (!customStart || !customEnd)) return;
    fetchActivity();
  }, [fetchActivity, range, customStart, customEnd]);

  useEffect(() => {
    citiesInitialized.current = false;
    setSelectedCities([]);
  }, [range, customStart, customEnd]);

  useEffect(() => {
    if (citiesInitialized.current || !data?.cities?.list?.length) return;
    setSelectedCities(data.cities.list.slice(0, 5).map((row) => row.city));
    citiesInitialized.current = true;
  }, [data]);

  const summary = data?.summary || {};
  const cities = data?.cities?.list || [];
  const heatmapMax = useMemo(() => {
    let max = 0;
    (data?.heatmap?.cities || []).forEach((row) => {
      (row.cells || []).forEach((cell) => {
        if (cell.count > max) max = cell.count;
      });
    });
    return max;
  }, [data]);

  const cityCsv = cities.map((row) => ({
    rank: row.rank,
    city: row.city,
    active_users: row.count,
    share_percent: row.share,
    previous_count: row.previousCount,
    change_percent: row.changePercent ?? '',
  }));
  const userCsv = (data?.activeUsers?.list || []).map((user) => ({
    name: user.profileName,
    email: user.email,
    city: user.city,
    last_seen: user.lastSeen,
    status: user.presence,
    verified: user.isVerified,
  }));

  const kpis = [
    {
      label: 'Unique active users',
      value: summary.uniqueActive,
      change: summary.uniqueActiveChange,
      icon: Users,
      hint: 'Distinct users with lastSeen in this period',
    },
    {
      label: 'Currently online',
      value: summary.onlineNow,
      change: null,
      icon: Wifi,
      hint: 'Live presence (isOnline), not the date filter',
    },
    {
      label: 'New users',
      value: summary.newRegistrations,
      change: summary.newRegistrationsChange,
      icon: UserPlus,
      hint: 'Accounts created in this period',
    },
    {
      label: 'Total users',
      value: summary.totalUsers,
      change: null,
      icon: Globe,
      hint: 'All non-deleted registered accounts',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">User Activity</h1>
          <p className="mt-1 text-sm text-slate-500">
            Track active users, city-wise engagement and growth. Timezone {data?.timezone || 'Asia/Kolkata'}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => { setRange(option.value); setPage(1); }}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                range === option.value
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-purple-300'
              }`}
            >
              {option.label}
            </button>
          ))}
          {range === 'custom' && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5">
              <CalendarRange className="h-4 w-4 text-purple-500" />
              <input type="date" value={customStart} max={customEnd || undefined} onChange={(e) => { setCustomStart(e.target.value); setPage(1); }} className="text-sm outline-none" />
              <span className="text-slate-400">to</span>
              <input type="date" value={customEnd} min={customStart || undefined} onChange={(e) => { setCustomEnd(e.target.value); setPage(1); }} className="text-sm outline-none" />
            </div>
          )}
        </div>
      </div>

      {data?.dataQuality?.dailyNote && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {data.dataQuality.dailyNote} Online status uses live presence. City uses each user’s saved city, with missing values as Unknown.
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
          <button type="button" onClick={fetchActivity} className="ml-3 font-semibold underline">Retry</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 text-white">
                  <Icon className="h-5 w-5" />
                </div>
                {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
              <p className="text-sm font-medium text-slate-500">{card.label}</p>
              <p className="mt-1 text-3xl font-bold text-slate-900">{formatNumber(card.value)}</p>
              <div className="mt-2"><Change value={card.change} /></div>
              <p className="mt-2 text-xs text-slate-400">{card.hint}</p>
            </div>
          );
        })}
      </div>

      <CityActivityTrends
        loading={loading}
        focusCity={focusCity}
        onFocusCity={setFocusCity}
        focusSeries={focusSeries}
        onToggleSeries={(key) => setFocusSeries((current) => ({ ...current, [key]: !current[key] }))}
        data={data?.focusCities}
        period={data?.period}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm xl:col-span-3">
          <div className="mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-bold text-slate-900">Active users over time</h2>
          </div>
          <div className="h-72">
            {(data?.dailyCounts || []).length ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.dailyCounts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="activeCount" name="Active users" stroke="#7c3aed" fill="#ddd6fe" />
                  <Line type="monotone" dataKey="newCount" name="New users" stroke="#ec4899" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart loading={loading} />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-bold text-slate-900">Users by city</h2>
            </div>
            <CSVLink data={cityCsv} filename="strapin-city-activity.csv" className="inline-flex items-center gap-1 text-sm font-semibold text-purple-700">
              <Download className="h-4 w-4" /> Export
            </CSVLink>
          </div>
          <p className="mb-3 text-xs text-slate-500">
            {formatNumber(data?.cities?.cityCount)} cities · {formatNumber(data?.cities?.totalUnique)} unique active users
          </p>
          <div className="mb-3 flex gap-2">
            <input
              value={citySearch}
              onChange={(e) => { setCitySearch(e.target.value); setPage(1); }}
              placeholder="Search cities"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-purple-400"
            />
            <select
              value={citySort}
              onChange={(e) => setCitySort(e.target.value)}
              className="rounded-xl border border-slate-200 px-2 py-2 text-sm"
            >
              <option value="count_desc">Most active</option>
              <option value="count_asc">Least active</option>
              <option value="name">Name</option>
            </select>
          </div>
          <div className="h-48">
            {cities.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cities.slice(0, 8)} layout="vertical" margin={{ left: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="city" width={88} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {cities.slice(0, 8).map((row) => (
                      <Cell key={row.city} fill={row.city === 'Unknown' ? '#94a3b8' : '#8b5cf6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart loading={loading} />
            )}
          </div>
          <div className="mt-3 max-h-56 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-white text-slate-500">
                <tr>
                  <th className="py-2">City</th>
                  <th className="py-2 text-right">Active</th>
                  <th className="py-2 text-right">Share</th>
                  <th className="py-2 text-right">Change</th>
                </tr>
              </thead>
              <tbody>
                {cities.map((row) => (
                  <tr key={row.city} className="border-t border-slate-100">
                    <td className="py-2 font-medium text-slate-800">{row.rank}. {row.city}</td>
                    <td className="py-2 text-right">{formatNumber(row.count)}</td>
                    <td className="py-2 text-right text-slate-500">{row.share}%</td>
                    <td className="py-2 text-right"><Change value={row.changePercent} /></td>
                  </tr>
                ))}
                {!cities.length && !loading && (
                  <tr><td colSpan={4} className="py-6 text-center text-slate-400">No city activity in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-bold text-slate-900">City-wise activity trend</h2>
          <div className="mb-3 flex flex-wrap gap-2">
            {cities.slice(0, 8).map((row) => {
              const on = selectedCities.includes(row.city);
              return (
                <button
                  key={row.city}
                  type="button"
                  onClick={() => setSelectedCities((current) => (
                    on ? current.filter((city) => city !== row.city) : [...current, row.city].slice(0, 6)
                  ))}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${on ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  {row.city}
                </button>
              );
            })}
          </div>
          <div className="h-64">
            {(data?.cityTrend?.series || []).length ? (
              <CityTrendChart series={data.cityTrend.series} />
            ) : (
              <EmptyChart loading={loading} />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-bold text-slate-900">City activity heatmap</h2>
          <div className="mb-2 flex items-center gap-2 text-xs text-slate-500">
            <span>Less</span>
            {PURPLE_SCALE.map((color) => (
              <span key={color} className="h-3 w-3 rounded-sm" style={{ backgroundColor: color }} />
            ))}
            <span>More</span>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white py-1 pr-2 text-left">City</th>
                  {(data?.heatmap?.days || []).map((day) => (
                    <th key={day} className="px-1 py-1 font-medium text-slate-500">{day.slice(5)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.heatmap?.cities || []).map((row) => (
                  <tr key={row.city}>
                    <td className="sticky left-0 bg-white py-1 pr-2 font-medium text-slate-700">{row.city}</td>
                    {(row.cells || []).map((cell) => (
                      <td key={cell.date} title={`${row.city} ${cell.date}: ${cell.count}`}>
                        <div
                          className="m-0.5 flex h-7 w-7 items-center justify-center rounded-md text-[10px] font-semibold text-slate-700"
                          style={{ backgroundColor: heatColor(cell.count, heatmapMax) }}
                        >
                          {cell.count || ''}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!data?.heatmap?.cities?.length && !loading && (
              <p className="py-8 text-center text-slate-400">No heatmap cells for this range.</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Currently active users ({formatNumber(data?.activeUsers?.pagination?.total)})</h2>
            <p className="text-xs text-slate-500">Users whose lastSeen falls in the selected period. Online uses live presence.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                placeholder="Search name, email or city"
                className="rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-purple-400"
              />
            </div>
            <select value={cityFilter} onChange={(e) => { setCityFilter(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="">All cities</option>
              {cities.map((row) => <option key={row.city} value={row.city}>{row.city}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="all">All status</option>
              <option value="online">Online</option>
              <option value="recent">Active recently</option>
            </select>
            <select value={verifiedFilter} onChange={(e) => { setVerifiedFilter(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="all">All verification</option>
              <option value="true">Verified</option>
              <option value="false">Unverified</option>
            </select>
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
            <CSVLink data={userCsv} filename="strapin-active-users.csv" className="inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-purple-700">
              <Download className="h-4 w-4" /> Export
            </CSVLink>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="py-3">User</th>
                <th className="py-3">Email</th>
                <th className="py-3">City</th>
                <th className="py-3">Last active</th>
                <th className="py-3">Status</th>
                <th className="py-3">Verified</th>
                <th className="py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.activeUsers?.list || []).map((user) => (
                <tr key={user._id} className="border-t border-slate-100">
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      {user.avatar ? (
                        <img src={user.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-700">
                          {(user.profileName || user.email || '?').slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <span className="font-semibold text-slate-800">{user.profileName || 'No name'}</span>
                    </div>
                  </td>
                  <td className="py-3 text-slate-600">{user.email || '—'}</td>
                  <td className="py-3 text-slate-600">{user.city}</td>
                  <td className="py-3 text-slate-600">{relativeTime(user.lastSeen)}</td>
                  <td className="py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      user.presence === 'online'
                        ? 'bg-emerald-100 text-emerald-700'
                        : user.presence === 'recent'
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-slate-100 text-slate-600'
                    }`}>
                      {user.presence === 'online' ? 'Online' : user.presence === 'recent' ? 'Active recently' : 'Offline'}
                    </span>
                  </td>
                  <td className="py-3">{user.isVerified ? <span className="font-semibold text-emerald-600">Yes</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="py-3">
                    <Link to={`/users/${user._id}`} className="font-semibold text-purple-700 hover:text-purple-900">View</Link>
                  </td>
                </tr>
              ))}
              {!data?.activeUsers?.list?.length && !loading && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400">No users match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <p>
            Page {data?.activeUsers?.pagination?.page || 1} of {data?.activeUsers?.pagination?.pages || 1}
          </p>
          <div className="flex gap-2">
            <button type="button" disabled={(data?.activeUsers?.pagination?.page || 1) <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))} className="rounded-xl bg-slate-100 px-4 py-2 font-semibold disabled:opacity-40">Previous</button>
            <button type="button" disabled={(data?.activeUsers?.pagination?.page || 1) >= (data?.activeUsers?.pagination?.pages || 1)} onClick={() => setPage((value) => value + 1)} className="rounded-xl bg-slate-100 px-4 py-2 font-semibold disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CityActivityTrends({ loading, focusCity, onFocusCity, focusSeries, onToggleSeries, data, period }) {
  const table = data?.table || [];
  const selected = table.find((row) => row.city === focusCity) || null;
  const chartPoints = data?.series?.[focusCity] || [];
  const hasCityData = selected && (
    selected.totalRegistered > 0
    || selected.activeUsers > 0
    || selected.newRegistrations > 0
    || selected.returningUsers > 0
    || selected.deletedAccounts > 0
  );
  const cityCards = [
    {
      label: 'Total registered users',
      value: selected?.totalRegistered,
      icon: Users,
      hint: 'Lifetime accounts classified as this city. Not filtered by the activity period.',
    },
    {
      label: 'Active users',
      value: selected?.activeUsers,
      icon: Activity,
      hint: 'Unique users whose lastSeen falls in the selected period.',
    },
    {
      label: 'New registrations',
      value: selected?.newRegistrations,
      icon: UserPlus,
      hint: 'Accounts created during the selected period.',
    },
    {
      label: 'Returning users',
      value: selected?.returningUsers,
      icon: RotateCcw,
      hint: 'Registered before this period and active in it.',
    },
    {
      label: 'Deleted accounts',
      value: selected?.deletedAccounts,
      icon: UserMinus,
      hint: 'Accounts with deletedAt in the selected period.',
    },
  ];
  const seriesKeys = [
    { key: 'activeCount', name: 'Daily active users', color: '#7c3aed' },
    { key: 'newCount', name: 'New registrations', color: '#ec4899' },
    { key: 'returningCount', name: 'Returning users', color: '#0ea5e9' },
  ];

  return (
    <div className="space-y-4 rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">City Activity Trends</h2>
          <p className="mt-1 text-sm text-slate-500">
            Pune and Mumbai only. Uses saved city from location metadata. Period {period?.startYmd || '—'} to {period?.endYmd || '—'} ({period?.definition || 'same filters as the rest of this page'}).
          </p>
        </div>
        <div className="flex rounded-full border border-slate-200 bg-slate-50 p-1">
          {['Pune', 'Mumbai'].map((city) => (
            <button
              key={city}
              type="button"
              onClick={() => onFocusCity(city)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                focusCity === city
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-purple-700'
              }`}
            >
              {city}
            </button>
          ))}
        </div>
      </div>

      {data?.classification?.note && (
        <p className="text-xs text-slate-500">{data.classification.note} {data.deletedNote} {data.dailyNote}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {cityCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 text-white">
                  <Icon className="h-4 w-4" />
                </div>
                {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
              <p className="text-xs font-medium text-slate-500">{card.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{formatNumber(card.value)}</p>
              <p className="mt-2 text-xs text-slate-400">{card.hint}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-900">{focusCity} daily activity</h3>
            <div className="flex flex-wrap gap-2">
              {seriesKeys.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onToggleSeries(item.key)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    focusSeries[item.key] ? 'text-white' : 'bg-slate-100 text-slate-500'
                  }`}
                  style={focusSeries[item.key] ? { backgroundColor: item.color } : undefined}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
          <div className="h-72">
            {hasCityData && chartPoints.length && seriesKeys.some((item) => focusSeries[item.key]) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartPoints}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  {seriesKeys.filter((item) => focusSeries[item.key]).map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.name}
                      stroke={item.color}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                {loading
                  ? 'Loading city activity…'
                  : !seriesKeys.some((item) => focusSeries[item.key])
                    ? 'Turn on at least one series to view the chart.'
                    : `No classified ${focusCity} users to chart for this period.`}
              </div>
            )}
          </div>
        </div>
        <div className="xl:col-span-2 overflow-x-auto">
          <h3 className="mb-3 font-semibold text-slate-900">Pune vs Mumbai</h3>
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="py-2">City</th>
                <th className="py-2 text-right">Registered</th>
                <th className="py-2 text-right">Active</th>
                <th className="py-2 text-right">New</th>
                <th className="py-2 text-right">Returning</th>
                <th className="py-2 text-right">Deleted</th>
                <th className="py-2 text-right">Active Δ</th>
              </tr>
            </thead>
            <tbody>
              {table.map((row) => (
                <tr
                  key={row.city}
                  className={`border-t border-slate-100 ${row.city === focusCity ? 'bg-purple-50/70' : ''}`}
                >
                  <td className="py-2 font-semibold text-slate-800">{row.city}</td>
                  <td className="py-2 text-right">{formatNumber(row.totalRegistered)}</td>
                  <td className="py-2 text-right">{formatNumber(row.activeUsers)}</td>
                  <td className="py-2 text-right">{formatNumber(row.newRegistrations)}</td>
                  <td className="py-2 text-right">{formatNumber(row.returningUsers)}</td>
                  <td className="py-2 text-right">{formatNumber(row.deletedAccounts)}</td>
                  <td className="py-2 text-right"><Change value={row.activeChange} /></td>
                </tr>
              ))}
              {!table.length && !loading && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">No Pune or Mumbai city data available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CityTrendChart({ series }) {
  const dates = series[0]?.points?.map((point) => point.date) || [];
  const chartData = dates.map((date) => {
    const row = { date };
    series.forEach((item) => {
      row[item.city] = item.points.find((point) => point.date === date)?.count || 0;
    });
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        {series.map((item, index) => (
          <Line
            key={item.city}
            type="monotone"
            dataKey={item.city}
            stroke={PURPLE_SCALE[index + 2] || '#7c3aed'}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function EmptyChart({ loading }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-slate-400">
      {loading ? 'Loading chart…' : 'No data for this period.'}
    </div>
  );
}

export default UserActivity;
